import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Folder, ChevronRight, Loader2, CornerDownRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Browse INTO an example project folder (under the projects root) and select the
 * subfolder where PDFs should go. The relative path (levels beyond the project
 * folder) becomes the routing template applied to every project.
 */
export function RoutingPathPicker({ open, onOpenChange, rootId, rootName, onPick }) {
  const [stack, setStack] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const current = stack[stack.length - 1];
  // depth 0 = root, depth 1 = an example project, depth >=2 = inside project (routing target)
  const depth = stack.length - 1;
  const relPath = stack.slice(2).map((s) => s.name).join("/");

  const load = async (folderId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/storage/folders?folder_id=${folderId}`);
      setItems((data.entries || []).filter((e) => e.type === "folder"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Failed to load folders");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && rootId) {
      const base = [{ id: rootId, name: rootName || "Projects Root" }];
      setStack(base); load(rootId);
    }
  }, [open, rootId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openFolder = (f) => { setStack((s) => [...s, f]); load(f.id); };
  const goTo = (idx) => { const f = stack[idx]; setStack(stack.slice(0, idx + 1)); load(f.id); };

  const hint = depth === 0
    ? "Open an example project folder to begin."
    : depth === 1
      ? "You're at the project level. Open the subfolder where PDFs should be filed."
      : `PDFs will be routed to: ${relPath || "(project root)"}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="routing-path-picker">
        <DialogHeader>
          <DialogTitle>Select routing subfolder</DialogTitle>
          <DialogDescription>Browse one example project and pick the destination subfolder. The same relative path is applied to every project.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1 text-sm flex-wrap">
          {stack.map((s, i) => (
            <span key={s.id} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
              <button onClick={() => goTo(i)} className="hover:text-primary">{s.name}</button>
            </span>
          ))}
        </div>
        <div className="rounded-md bg-secondary px-3 py-2 text-xs flex items-center gap-2">
          <CornerDownRight className="h-3.5 w-3.5 text-primary" />{hint}
        </div>
        <div className="border border-border rounded-md h-56 overflow-y-auto">
          {loading ? (
            <div className="h-full flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No subfolders here</div>
          ) : (
            items.map((f) => (
              <div key={f.id} className="flex items-center justify-between px-3 py-2 border-b border-border hover:bg-accent" data-testid={`routing-folder-${f.id}`}>
                <button onClick={() => openFolder(f)} className="flex items-center gap-2 flex-1 text-left">
                  <Folder className="h-4 w-4 text-primary" /> {f.name}
                </button>
                <Button size="sm" variant="outline" onClick={() => openFolder(f)}>Open</Button>
              </div>
            ))
          )}
        </div>
        <Button disabled={depth < 1} onClick={() => onPick(relPath)} data-testid="use-routing-path">
          {depth < 1 ? "Open a project folder first" : `Use this path: ${relPath || "(project root)"}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
